import { Box, DateField, Icon, TextField, TextFieldProps, TextFieldSize } from '@bechtel/gds';
import '@bechtel/gds/dist/index.css';
import styled, { css } from 'styled-components';
import { FormLabelWidth } from '~/utilities';
import { FormLabelSize, InputAppearance } from './types';

interface FormInputProps {
  appearance?: InputAppearance;
  disabled?: boolean;
  readOnly?: boolean;
  isMainCommodity?: boolean;
  error?: boolean;
  changed?: boolean;
  focus?: boolean;
  size?: FormLabelSize;
}
interface BoxStyledProps {
  isMainCommodity?: boolean;
}
interface ErrorBoxStyledProps {
  error: boolean;
}

type TextFieldSizeProps = FormInputProps & TextFieldProps;

function getInputWrapperStyles(appearance: InputAppearance | undefined) {
  switch (appearance) {
    case InputAppearance.SPECIAL:
      return css`
        border: 1px solid ${({ theme }) => theme.colors.yellow[500]};
      `;
    case InputAppearance.DEFAULT:
      return css`
        border: 1px solid ${({ theme }) => theme.colors.grey[300]};
        &:hover {
          border: 1px solid ${({ theme }) => theme.colors.blue[300]};
        }
      `;
    default:
      return css`
        border: 1px solid ${({ theme }) => theme.colors.grey[300]};
      `;
  }
}

function getLabelWrapperStyles(appearance: InputAppearance | undefined) {
  switch (appearance) {
    case InputAppearance.SPECIAL:
      return css`
        background-color: ${({ theme }) => theme.colors.yellow[100]};
      `;
    case InputAppearance.DEFAULT:
      return css`
        background-color: ${({ theme }) => theme.colors.grey[100]};
      `;
    default:
      return css`
        background-color: ${({ theme }) => theme.colors.grey[100]};
      `;
  }
}

function getLabelWidth(size: FormLabelSize | undefined) {
  switch (size) {
    case FormLabelSize.XSMALL:
      return css`
        min-width: ${FormLabelWidth.XSMALL};
      `;
    case FormLabelSize.SMALL:
    default:
      return css`
        width: ${FormLabelWidth.DEFAULT};
      `;
    case FormLabelSize.MEDIUM:
      return css`
        width: ${FormLabelWidth.MEDIUM};
      `;
    case FormLabelSize.LARGE:
      return css`
        width: ${FormLabelWidth.LARGE};
      `;
  }
}

export const LabelWrapper = styled(Box).attrs({
  alignItems: { sm: 'center' },
  px: { sm: '450' },
  py: { sm: '300' },
})<FormInputProps>`
  min-width: ${FormLabelWidth.DEFAULT};
  ${({ theme, appearance, size }) => css`
    border-radius: ${theme.radii[200]} 0 0 ${theme.radii[200]};
    border-right: 1px solid ${theme.colors.grey[300]};
    ${getLabelWrapperStyles(appearance)};
    ${getLabelWidth(size)};
    font-size: ${({ theme }) => theme.fontSizes[300]};
  `}
  ${({ changed, theme, size }) =>
    changed &&
    css`
      ::before {
        content: '';
        position: absolute;
        left: 0;
        ${getLabelWidth(size)};
        height: 100%;
        background-color: ${theme.colors.blue[200]};
        border-radius: ${theme.radii[200]} 0 0 ${theme.radii[200]};
        opacity: 0.5;
      }
    `}
  ${({ error, theme, size }) =>
    error &&
    css`
      ::before {
        content: '';
        position: absolute;
        left: 0;
        ${getLabelWidth(size)};
        height: 100%;
        background-color: ${theme.colors.red[200]};
        border-radius: ${theme.radii[200]} 0 0 ${theme.radii[200]};
        opacity: 0.5;
      }
    `}
  ${({ readOnly }) =>
    readOnly &&
    css`
      background-color: ${({ theme }) => theme.colors.grey[200]};
    `}
    ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${({ theme }) => theme.colors.grey[200]};
      opacity: 0.5;
    `}
    ${({ appearance }) =>
    appearance === InputAppearance.SPECIAL &&
    css`
      background-color: ${({ theme }) => theme.colors.yellow[100]};
      opacity: 1;
    `}
`;

export const InputWrapperStyled = styled(Box).attrs({
  flexGrow: { sm: 1 },
})<FormInputProps>`
  ${({ theme, appearance }) => css`
    border-radius: ${theme.radii[200]};
    ${getInputWrapperStyles(appearance)};
  `}
  ${({ theme, changed }) =>
    changed &&
    css`
      border: 1px solid ${theme.colors.blue[500]};
    `}
    ${({ theme, focus }) =>
    focus &&
    css`
      border: 1px solid ${theme.colors.blue[500]};
    `}
  ${({ theme, error }) =>
    error &&
    css`
      border: 1px solid ${theme.colors.red[700]};
      &:hover {
        border: 1px solid ${theme.colors.red[700]};
      }
    `}
  ${({ theme, disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      opacity: 1;
      border: 1px solid ${theme.colors.grey[300]};
    `}
  ${({ theme, isMainCommodity }) =>
    isMainCommodity &&
    css`
      position: relative;
      &::before {
        content: '';
        z-index: 1;
        position: absolute;
        width: 3px;
        height: 100%;
        left: 0;
        top: 0;
        border-radius: ${theme.spacings[200]} 0 0 ${theme.spacings[200]};
        background-color: ${theme.colors.purple[500]};
      }
    `}
`;

export const CharTextContainerStyled = styled(Box)`
  ${({ theme }) => css`
    font-size: ${theme.fontSizes[200]};
    color: ${theme.colors.grey[600]};
  `}
  height: 0;
  align-self: self-end;
`;
export const TextFieldStyled = styled(TextField).attrs({
  size: TextFieldSize.SMALL,
})<TextFieldSizeProps>`
  ${({ theme }) => css`
    height: 26px;
    font-size: ${theme.fontSizes[300]};
    color: ${theme.colors.black};
    background-color: ${theme.colors.white};
    &:focus {
      border-color: transparent;
      box-shadow: none;
    }
  `}
  ${({ convertToUpperCase }) =>
    convertToUpperCase &&
    css`
      text-transform: uppercase;
    `}
  ${({ changed }) =>
    changed &&
    css`
      background-color: ${({ theme }) => theme.colors.blue[100]};
    `}

  ${({ error }) =>
    error &&
    css`
      background-color: ${({ theme }) => theme.colors.red[100]};
    `}
    ${({ readOnly }) =>
    readOnly &&
    css`
      background-color: ${({ theme }) => theme.colors.grey[100]};
    `}
    ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${({ theme }) => theme.colors.grey[100]};
      opacity: 0.5;
    `}
    ${({ appearance }) =>
    appearance === InputAppearance.SPECIAL &&
    css`
      background-color: ${({ theme }) => theme.colors.yellow[100]};
      border: transparent;
    `}
`;

export const DateFieldStyled = styled(DateField)`
  height: 26px;
  &:focus {
    box-shadow: none;
  }
  border-radius: none;
  border: transparent;
  ${({ changed }) =>
    changed &&
    css`
      background-color: ${({ theme }) => theme.colors.blue[100]};
    `}

  ${({ error }) =>
    error &&
    css`
      background-color: ${({ theme }) => theme.colors.red[100]};
    `}
    ${({ readOnly }) =>
    readOnly &&
    css`
      background-color: ${({ theme }) => theme.colors.grey[100]};
    `}
    ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${({ theme }) => theme.colors.grey[100]};
      opacity: 0.7;
    `}

    ${({ appearance }) =>
    appearance === InputAppearance.SPECIAL &&
    css`
      background-color: ${({ theme }) => theme.colors.yellow[100]};
      border: transparent;
      opacity: 1;
    `}
`;

export const BoxStyled = styled(Box).attrs({
  alignItems: { sm: 'center' },
  justifyContent: { sm: 'center' },
  alignSelf: { sm: 'center' },
})<BoxStyledProps>`
  border-radius: ${({ theme }) => theme.spacings[100]};
  cursor: pointer;
  position: absolute;

  ${({ theme, isMainCommodity }) =>
    isMainCommodity
      ? css`
          background-color: ${theme.colors.primary[700]};
          top: 3px;
          height: ${theme.spacings[550]};
          min-width: ${theme.spacings[550]};
        `
      : css`
          margin: ${theme.spacings[100]};
          right: ${theme.spacings[100]};
          background-color: ${theme.colors.grey[200]};
          height: 22px;
          min-width: 26px;
        `}
`;

export const MainCommodityStyled = styled(Box).attrs({
  pl: { sm: '450' },
})`
  ${({ theme }) =>
    css`
      width: ${theme.spacings[450]};
      height: ${theme.spacings[600]};
      position: absolute;
      right: -${theme.spacings[200]};
    `}
`;

export const ErrorIconStyled = styled(Icon)`
  margin-left: ${({ theme }) => theme.spacings[300]};
  cursor: pointer;
`;

export const ErrorFieldStyled = styled(Box)<ErrorBoxStyledProps>`
  ${({ theme, error }) =>
    error &&
    css`
      background: ${theme.colors.red[100]};
      padding-top: ${theme.spacings[300]};
      padding-left: ${theme.spacings[300]};
    `}
`;

export const SelectBoxStyled = styled(Box).attrs({
  alignItems: { sm: 'center' },
  flexGrow: { sm: 1 },
})<TextFieldSizeProps>`
  ${({ theme }) => css`
    height: 26px;
    font-size: ${theme.fontSizes[300]};
    color: ${theme.colors.black};
    background-color: ${theme.colors.white};
    & button:focus {
      border-color: transparent;
      box-shadow: none;
    }
    ${({ changed }) =>
      changed &&
      css`
        background-color: ${({ theme }) => theme.colors.blue[100]};
      `}
    ${({ error }) =>
      error &&
      css`
        background-color: ${({ theme }) => theme.colors.red[100]};
      `}
    ${({ readOnly }) =>
      readOnly &&
      css`
        background-color: ${({ theme }) => theme.colors.grey[100]};
        pointer-events: none;
      `}
    ${({ disabled }) =>
      disabled &&
      css`
        background-color: ${({ theme }) => theme.colors.grey[100]};
        opacity: 0.7;
      `}

    ${({ appearance }) =>
      appearance === InputAppearance.SPECIAL &&
      css`
        background-color: ${({ theme }) => theme.colors.yellow[100]};
        border: transparent;
        opacity: 1;
      `}
  `}
`;
